import { Controller } from "@hotwired/stimulus";
import StimulusReflex from "stimulus_reflex";

// Autocomplete search for appellation records, backed by AppellationFormSearchReflex.
//
// Supports two modes via Stimulus values (defaults preserve the original
// single-select behavior used by appellations/_form):
//   - multiple: false -> single hidden input (selectedId target), input shows name
//   - multiple: true  -> chips + repeated hidden inputs (selectedIds/selected targets)
// The hidden-input name for multi-select is configurable via paramName.
export default class extends Controller {
  static targets = [
    "input",
    "results",
    "selectedId",
    "selectedIds",
    "selected",
    "spinner",
  ];
  static values = {
    selectedId: String,
    multiple: { type: Boolean, default: false },
    paramName: { type: String, default: "" },
  };

  connect() {
    StimulusReflex.register(this);
  }

  search() {
    const query = this.inputTarget.value.trim();
    if (query.length < 2) {
      this.resultsTarget.classList.add("hidden");
      return;
    }

    this.element.dataset.query = query;

    this.showSpinner();
    this.resultsTarget.classList.remove("hidden");
    this.resultsTarget.classList.add("opacity-50");

    this.stimulate("AppellationFormSearch#search");
  }

  beforeSearch() {
    this.resultsTarget.classList.add("opacity-50");
    this.showSpinner();
  }

  searchSuccess() {
    this.resultsTarget.classList.remove("opacity-50");
    this.hideSpinner();
  }

  searchError(error) {
    console.error("Appellation search error:", error);
    this.resultsTarget.classList.remove("opacity-50");
    this.resultsTarget.classList.add("hidden");
    this.hideSpinner();
  }

  select(event) {
    const appellationId = event.currentTarget.dataset.appellationId;
    const appellationName = event.currentTarget.dataset.appellationName;

    if (this.multipleValue) {
      this.addSelectedAppellation(appellationId, appellationName);
      this.inputTarget.value = "";
      this.selectedIdsTarget.dispatchEvent(new Event("change"));
    } else {
      this.selectedIdTarget.value = appellationId;
      this.inputTarget.value = appellationName;
      this.inputTarget.dispatchEvent(new Event("change"));
      this.selectedIdTarget.dispatchEvent(new Event("change"));
    }

    this.resultsTarget.classList.add("hidden");
  }

  removeAppellation(event) {
    const appellationId = event.currentTarget.dataset.appellationId;

    event.currentTarget.closest("[data-appellation-item]").remove();

    const hiddenInput = this.selectedIdsTarget.querySelector(
      `input[value="${appellationId}"]`
    );
    if (hiddenInput) {
      hiddenInput.remove();
    }

    this.selectedIdsTarget.dispatchEvent(new Event("change"));
  }

  addSelectedAppellation(appellationId, appellationName) {
    if (
      this.selectedIdsTarget.querySelector(`input[value="${appellationId}"]`)
    ) {
      return;
    }

    const hiddenInput = document.createElement("input");
    hiddenInput.type = "hidden";
    hiddenInput.name = this.paramNameValue;
    hiddenInput.value = appellationId;
    this.selectedIdsTarget.appendChild(hiddenInput);

    const selectedItem = document.createElement("div");
    selectedItem.className =
      "flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2";
    selectedItem.setAttribute("data-appellation-item", appellationId);

    selectedItem.innerHTML = `
      <span class="text-sm font-medium text-indigo-900">${appellationName}</span>
      <button type="button"
              class="text-indigo-600 hover:text-indigo-800 ml-2 transition-colors duration-200"
              data-action="click->appellation-form-search#removeAppellation"
              data-appellation-id="${appellationId}">
        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    `;

    this.selectedTarget.appendChild(selectedItem);
  }

  showSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("hidden");
    }
  }

  hideSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden");
    }
  }

  receiveResults(html) {
    this.resultsTarget.innerHTML = html;
    this.resultsTarget.classList.remove("hidden");
  }
}
